
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Button, Message, Space, Tag } from '@bedrock/components';
import closingReportTableModal from './ClosingReportTableModal';
import { ChevronRight, Edit } from '@bedrock/icons-react';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { $sType } from '#utils';
import { apiService } from '#services';
import { DemandAiReviewResultDto } from '#services/api';
import { ButtonProps } from '@bedrock/components/es/Button';

type ClosingReportBtnProps = {
    value?: any;
    onChange?: (value: any) => void;
    view?: boolean;
    cooperateAccountId?: number;
    detail: any;
    buttonProps?: ButtonProps;
}

// 结案明细按钮组件，支持查看和编辑结案明细
const ClosingReportBtn = forwardRef((props: ClosingReportBtnProps, ref) => {
    const { detail, view = false, cooperateAccountId } = props;
    const [localValue, setLocalValue] = useControllableValue(props);
    // 表格列配置
    const [columns, setColumns] = useState([]);
    // AI审核结果
    const [aiReviewResult, setAiReviewResult] = useState<DemandAiReviewResultDto>();
    // 报价数据
    const [quoteData, setQuoteData] = useState([]);
    // 加载状态
    const [loading, setLoading] = useState(false);

    const [saveTime, setSaveTime] = useState('');

    // 初始化结案明细数据，将settleReports与quoteData进行匹配
    const initQuoteDataFn = useMemoizedFn((quoteData, settleReports) => {
        let quoteKeyMap = {};
        const reports = quoteData?.reduce?.((acc, item) => {
            // 根据quoteKey在settle_reports中找到对应的report
            const report = settleReports?.find((report) => report.quote_key === item.quoteKey);
            if (report && !quoteKeyMap[item.quoteKey]) {
                acc.push(report);
                quoteKeyMap[item.quoteKey] = report;
            } else {
                acc.push({});
            }
            return acc;
        }, []) || [];

        // 通知父组件数据变更
        setLocalValue(reports);
    });

    // 初始化数据，获取结案明细相关数据
    const initDataFn = useMemoizedFn(async () => {
        if (!detail?.id || !detail?.demand_suppliers?.[0]?.id) return;
        setLoading(true);
        try {
            // 根据$sType选择不同的API
            const quoteSettleReportsApi = $sType === 'inner' ? apiService.demandQueryDemandSupplierQuoteSettleReports : apiService.outsourceDemandQueryDemandSupplierQuoteSettleReports;
            const res = await quoteSettleReportsApi({
                demand_supplier_id: detail.demand_suppliers?.[0]?.id,
            });
            const { columns, quote_data, settle_reports, demand_aireview_result } = res?.data || {};
            let reportList = settle_reports;
            setColumns(columns || []);
            setAiReviewResult(demand_aireview_result);
            let quoteData = quote_data;
            // 如果有合作内容ID，过滤对应的报价数据
            if (cooperateAccountId) {
                quoteData = quote_data.filter((item) => item.cooperateAccountId == cooperateAccountId);
            }
            setQuoteData(quoteData);
            // 外部编辑模式下，优先获取暂存的结构化结案报告数据
            if (!view && $sType === 'outer') {
                const res = await apiService.outsourceDemandGetSavedStructuredSettleReportsData({
                    demand_id: detail.id,
                });
                const { quote_settle_reports, saved_time } = res?.data || {};
                if (quote_settle_reports?.length) {
                    reportList = quote_settle_reports;
                    setSaveTime(saved_time);
                }
            }
            // 初始化结案明细
            initQuoteDataFn(quoteData, reportList);
            setLoading(false);  
        } catch (error) {
            setLoading(false);
            throw error;
        }
    });

    // 刷新 muse 行上传状态（用于关闭弹窗后更新 Tag）
    const refreshMuseStatus = useMemoizedFn(() => {
        if (hasMuseRows) {
            checkMuseUploadComplete().then(setMuseUploadReady);
        }
    });

    // 展示结案明细弹窗
    const showModalFn = useMemoizedFn(async () => {
        const { close } = closingReportTableModal.show({
            detail,
            reportList: localValue,
            view: view,
            cooperateAccountId,
            columns: columns || [],
            quoteData: quoteData || [],
            aiReviewResult: aiReviewResult,
            onOk: (data: any) => {
                setLocalValue(data);
                refreshMuseStatus();
            },
            onCancel: () => {
                refreshMuseStatus();
                close()
            },
        })
        // if (saveTime) {
        //     Message.info('已自动恢复上次 ' + saveTime + ' 暂存的内容');
        //     setSaveTime('');
        // }
    })

    // 是否存在 muse=1 的行
    const hasMuseRows = useMemo(() => {
        return quoteData?.some((item) => item.muse === 1);
    }, [quoteData]);

    // 判断每个quoteKey对应的report是否都已填写说明或者附件（仅校验非 muse 行）
    const valueReady = useMemo(() => {
        // 非 muse 行需要检查 content 或 attachments
        const nonMuseRows = quoteData?.filter((item) => item.muse !== 1) || [];
        // 没有非 muse 行时，非 muse 部分视为已完成
        if (nonMuseRows.length === 0) return true;
        if (!localValue?.length) return false;
        const quoteKeyMap = localValue?.reduce((acc, item) => {
            acc[item.quote_key] = item;
            return acc;
        }, {}) || {};
        return nonMuseRows.every((item) => {
            const report = quoteKeyMap[item.quoteKey];
            return report?.content || !!report?.attachments?.length;
        });
    }, [localValue, quoteData]);

    // muse 行上传完成状态
    const [museUploadReady, setMuseUploadReady] = useState(false);

    // 检查 muse 行上传状态
    const checkMuseUploadComplete = useMemoizedFn(async () => {
        if (!hasMuseRows || !detail?.id || $sType === 'inner') return true;
        try {
            const res = await apiService.outsourceV2MuseUploadCompleteCheckByDemandId({
                demand_id: detail.id,
            });
            return res?.data === true;
        } catch {
            return false;
        }
    });

    // 有 muse 行时更新 muse 上传完成状态（用于 Tag 显示）
    useEffect(() => {
        if (!hasMuseRows || view) return;
        checkMuseUploadComplete().then(setMuseUploadReady);
    }, [hasMuseRows, view, quoteData]);

    // 综合判断所有行是否填写完成（非 muse 行 + muse 行）
    const allReady = useMemo(() => {
        if (!hasMuseRows) return valueReady;
        return valueReady && museUploadReady;
    }, [hasMuseRows, valueReady, museUploadReady]);

    // 暴露校验方法给父组件
    useImperativeHandle(ref, () => ({
        validateFields: async () => {
            if (view) {
                // 只读模式直接通过
                return true;
            }
            // 校验非 muse 行
            if (!valueReady) {
                return Promise.reject('请完善结案明细');
            }
            // 校验 muse 行（调接口实时检查）
            if (hasMuseRows) {
                const museComplete = await checkMuseUploadComplete();
                if (!museComplete) {
                    return Promise.reject('请完成 Muse 素材上传');
                }
            }
            return true;
        }
    }));

    // 非查看模式下，初始化数据
    useEffect(() => {
        !view && initDataFn();
    }, [detail, view, cooperateAccountId]);

    // 如果未提交结构化结案报告且为查看模式，显示占位符
    if (!detail?.submitted_structured_settle_report && view) {
        return '--';
    }

    // 渲染按钮和状态标签
    return <Space size={8}>
        <Button
            onClick={async () => {
                // 查看模式下每次点击都重新拉取数据
                if (view) {
                    await initDataFn()
                }
                // 弹窗异步打开
                setTimeout(() => {
                    showModalFn()
                }, 1)
            }}
            loading={loading}
            type="checked"
            icon={!view ? <Edit /> : null}
            suffixIcon={view ? <ChevronRight /> : null}
            size="medium"
            className={'bg-white'}
            {...(props?.buttonProps || {})}
        >
            {props?.buttonProps?.children ? props?.buttonProps?.children : (view ? '查看结案明细' + (!cooperateAccountId ? '报告' : '') : '填写结案明细')}
        </Button>
        {/* 编辑模式下显示已提交/未提交状态 */}
        {!view && <Tag size="small" color={allReady ? 'light-green' : 'light-orange'} text={allReady ? '已填写' : '未填写'} />}
    </Space>
});

export default ClosingReportBtn;
